<script setup>
import AudienceActionsCell from "@frontJs/components/tables/cells/projectManagement/sma/AudienceActionsCell.vue";
import { PlusIcon } from "@heroicons/vue/24/solid";
import ActionButton from "@js/components/ActionButton.vue";
import Container from "@js/components/Container.vue";
import LoadingSpinner from "@js/components/LoadingSpinner.vue";
import DataTableLayout from "@js/components/tables/DataTableLayout.vue";
import CrossIconCell from "@js/components/tables/cells/CrossIconCell.vue";
import NumberCell from "@js/components/tables/cells/NumberCell.vue";
import PillCell from "@js/components/tables/cells/PillCell.vue";
import TextCellV2 from "@js/components/tables/cells/TextCellV2.vue";
import { useDataTable } from "@js/utils/dataTable";
import { formatDate } from "@js/utils/formatter";
import { useRoute } from "@js/utils/route";
import { createColumnHelper } from "@tanstack/vue-table";
import { h } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const { tenant } = defineProps({
  tenant: {
    type: Object,
    required: true,
  },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
  loading,
  table,
  count,
  globalFilter,
  filters,
  availableFilters,
  setGlobalFilter,
  setFilters,
  updateData,
  globalData,
} = useDataTable({
  columns: [
    columnHelper.accessor(() => null, {
      id: "name",
      header: () => t("label.name"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.name,
          length: 160,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "type",
      header: () => t("label.type"),
      cell: (props) =>
        h(PillCell, {
          pillValue: props.row.original.type.label,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "total",
      header: () => t("label.total"),
      cell: (props) =>
        props.row.original.total
          ? h(NumberCell, {
              number: props.row.original.total,
            })
          : h(CrossIconCell),
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "synchronizedAt",
      header: () => t("label.synchronizedAt"),
      cell: (props) =>
        props.row.original.synchronizedAt
          ? h(TextCellV2, {
              text: formatDate(props.row.original.synchronizedAt),
            })
          : h(CrossIconCell),
    }),
    columnHelper.display({
      id: "actions",
      header: t("label.actions"),
      cell: (innerProps) =>
        h(AudienceActionsCell, {
          tenant: tenant,
          row: innerProps.row,
          callback: updateData,
        }),
      width: 0,
    }),
  ],
  url: route("front.projectManagement.sma.postAudiences", {
    tenant: tenant.id,
  }),
  columnPinning: {
    right: ["actions"],
  },
});
</script>

<template>
  <LoadingSpinner v-if="loading" />

  <template v-else>
    <Container
      v-if="globalData.accountId && globalData.accountId !== ''"
      class="wysiwyg mb-4"
    >
      <ul>
        <li>
          Lien vers conditions générales des audiences personnalisées basées
          sur des listes clients (à accepter avant de créer/modifier/synchroniser une audience) :
          <ul>
            <li>
              <a
                :href="
                  'https://business.facebook.com/ads/manage/customaudiences/tos/?act=' +
                  globalData.accountId
                "
                target="_blank"
              >
                https://business.facebook.com/ads/manage/customaudiences/tos/?act={{
                  globalData.accountId
                }}
              </a>
            </li>
          </ul>
        </li>
      </ul>
    </Container>

    <DataTableLayout
      :table="table"
      :count="count"
      :global-filter="globalFilter"
      :filters="filters"
      :available-filters="availableFilters"
      :set-filters="setFilters"
      :set-global-filter="setGlobalFilter"
    >
      <template #actions>
        <ActionButton
          size="small"
          :href="
            route('front.projectManagement.sma.audience', {
              tenant: tenant.id,
            })
          "
          :left-icon="PlusIcon"
        >
          {{ t("button.add") }}
        </ActionButton>
      </template>
    </DataTableLayout>
  </template>
</template>
